import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { detectLanguageFromRegion } from '@/shared/utils/languageDetector';

// Import all translation files upfront (rollback lazy loading for stability)
import enCommon from './locales/en/common.json';
import enDocdiff from './locales/en/docdiff.json';
import enHcell from './locales/en/hcell.json';
import enHwp from './locales/en/hwp.json';
import enOcr from './locales/en/ocr.json';
import jaCommon from './locales/ja/common.json';
import jaHcell from './locales/ja/hcell.json';
import jaOcr from './locales/ja/ocr.json';
import koCommon from './locales/ko/common.json';
import koDocdiff from './locales/ko/docdiff.json';
import koHcell from './locales/ko/hcell.json';
import koHwp from './locales/ko/hwp.json';
import koOcr from './locales/ko/ocr.json';

const resources = {
  ko: {
    common: koCommon,
    hwp: koHwp,
    ocr: koOcr,
    hcell: koHcell,
    docdiff: koDocdiff,
  },
  en: {
    common: enCommon,
    hwp: enHwp,
    ocr: enOcr,
    hcell: enHcell,
    docdiff: enDocdiff,
  },
  ja: {
    common: jaCommon,
    ocr: jaOcr,
    hcell: jaHcell,
  },
};

// Custom language detector using centralized logic
// ✅ FIX: Use 'en' as fallback to prevent Korean flash for overseas users
const languageDetector = {
  type: 'languageDetector' as const,
  async: false,
  detect: () => detectLanguageFromRegion('en'),
  init: () => {},
  cacheUserLanguage: () => {},
};

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en', // ✅ FIX: Use 'en' as fallback for overseas users
    defaultNS: 'common',
    ns: ['common', 'hwp', 'ocr', 'hcell', 'docdiff'],

    // ✅ P1-5 FIX: Add missing key handler for better debugging
    saveMissing: import.meta.env.DEV, // Only in development
    missingKeyHandler: (lngs, ns, key) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error(`[i18n] Missing translation: ${ns}:${key} in ${lngs.join(', ')}`);
      }
    },

    interpolation: {
      escapeValue: false, // React already escapes values
      defaultVariables: {
        hcellSdkName: 'Hcell SDK',
      },
    },
  });

export default i18n;

// Helper function to get current language from URL
export const getLanguageFromUrl = (): 'ko' | 'en' | 'ja' => {
  const path = window.location.pathname;
  if (path.startsWith('/en')) return 'en';
  if (path.startsWith('/ja')) return 'ja';
  return 'ko';
};

// Helper function to update URL with new language
// isOverseas: overseas → ja gets /ja prefix, en is default (no prefix)
//             domestic → en gets /en prefix, ko is default (no prefix)
export const updateUrlLanguage = (newLang: string, isOverseas = false): string => {
  const path = window.location.pathname;

  // Remove any existing language prefix
  const basePath = path.replace(/^\/en(\/|$)/, '/').replace(/^\/ja(\/|$)/, '/') || '/';

  const suffix = basePath === '/' ? '' : basePath;
  if (isOverseas) {
    return newLang === 'ja' ? `/ja${suffix}` : basePath;
  } else {
    return newLang === 'en' ? `/en${suffix}` : basePath;
  }
};
